import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useTranslation } from 'react-i18next';
import { DOCUMENT } from '../constants/documentTheme';
import { partnerOutlineBtnSx, partnerOxbloodBtnSx } from './PartnerListChrome';
import {
  PARTNER_FINANCE_CURRENCIES,
  balanceDueFromForm,
  computePaymentStatus,
  financeToForm,
  formatMoney,
  todayYmd,
} from '../utils/partnerFinance';

export {
  financeToForm,
  financeFormToPayload,
  paymentStatusFromForm,
  balanceDueFromForm,
  rollupMemberFinance,
  formatMoney,
  computePaymentStatus,
} from '../utils/partnerFinance';

const STATUS_CHIP_SX = {
  not_invoiced: { borderColor: DOCUMENT.ruleHard, color: DOCUMENT.ink3 },
  deposit_due: { borderColor: '#ed6c02', color: '#ed6c02' },
  partially_paid: { borderColor: '#0288d1', color: '#0288d1' },
  paid: { borderColor: '#2e7d32', color: '#2e7d32' },
  overdue: { borderColor: DOCUMENT.oxblood, color: DOCUMENT.oxblood },
};

const sectionLabelSx = {
  fontSize: 11,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: DOCUMENT.ink3,
  mb: 1.5,
  fontWeight: 600,
};

const moneyDisplaySx = {
  fontFamily: DOCUMENT.display,
  fontSize: 22,
  lineHeight: 1.2,
  color: DOCUMENT.ink,
};

const fieldSx = { borderRadius: 0 };

/**
 * Paid amount and payment status as shown by the panel. When paidFromPayments
 * is set, the paid amount is the sum of recorded payments.
 */
export function resolvePanelPaymentStatus({ form, payments = [], paidFromPayments = false }) {
  const safeForm = form || financeToForm(null);
  const paidAmount = paidFromPayments
    ? payments.reduce((s, p) => s + (Number(p.amount) || 0), 0)
    : (safeForm.clientPaid === '' ? 0 : Number(safeForm.clientPaid));
  const status = computePaymentStatus({
    tripValue: safeForm.tripValue === '' ? null : safeForm.tripValue,
    clientPaid: paidAmount,
    paymentDueDate: safeForm.paymentDueDate,
  });
  return { paidAmount, status };
}

export function StatusBadge({ status, testId }) {
  const { t } = useTranslation();
  const key = {
    not_invoiced: 'statusNotInvoiced',
    deposit_due: 'statusDepositDue',
    partially_paid: 'statusPartiallyPaid',
    paid: 'statusPaid',
    overdue: 'statusOverdue',
  }[status] || 'statusNotInvoiced';
  return (
    <Chip
      size="small"
      variant="outlined"
      label={t(`partnerFinance.${key}`)}
      data-testid={testId}
      sx={STATUS_CHIP_SX[status] || STATUS_CHIP_SX.not_invoiced}
    />
  );
}

function MoneyLine({ label, value, emphasize = false, remaining = false }) {
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography sx={{ fontSize: 12, color: DOCUMENT.ink3, mb: 0.25 }}>{label}</Typography>
      <Typography
        sx={{
          ...moneyDisplaySx,
          fontSize: emphasize ? 26 : 20,
          color: remaining ? DOCUMENT.oxblood : DOCUMENT.ink,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

/**
 * Shared payment panel.
 * - full: client payment + agency earnings + history
 * - member: client payment + history (no commission)
 * - group: rollup totals + agency earnings
 */
const PartnerTripFinancePanel = ({
  form: formProp,
  onChange,
  onSave,
  saving = false,
  title,
  showTitle = true,
  showStatusChip = true,
  testId = 'partner-trip-finance',
  dense = false,
  embedded = false,
  variant = 'full',
  rollup = null,
  payments = [],
  paidFromPayments = false,
  onAddPayment,
  onDeletePayment,
  paymentSaving = false,
}) => {
  const { t } = useTranslation();
  const [form, setForm] = useState(formProp || financeToForm(null));
  const [paymentDraft, setPaymentDraft] = useState({
    amount: '',
    paymentDate: todayYmd(),
    method: 'credit_card',
    note: '',
  });

  useEffect(() => {
    if (formProp) setForm(formProp);
  }, [formProp]);

  const patchForm = (patch) => {
    setForm((prev) => ({ ...prev, ...patch }));
    onChange?.(patch);
  };

  const currency = form.currency || 'USD';
  const { paidAmount, status } = resolvePanelPaymentStatus({ form, payments, paidFromPayments });
  const balance = balanceDueFromForm({
    tripValue: form.tripValue,
    clientPaid: String(paidAmount),
  });

  const commissionOutstanding = (() => {
    const expected = form.commissionExpected === '' ? null : Number(form.commissionExpected);
    const received = form.commissionReceived === '' ? 0 : Number(form.commissionReceived);
    if (expected == null || Number.isNaN(expected) || Number.isNaN(received)) return null;
    return Math.max(0, Math.round((expected - received) * 100) / 100);
  })();

  const showClient = variant === 'full' || variant === 'member';
  const showAgency = variant === 'full' || variant === 'group';
  const showHistory = showClient && typeof onAddPayment === 'function';
  const showRollup = variant === 'group';

  const handleAddPayment = async () => {
    if (!onAddPayment) return;
    const amount = Number(paymentDraft.amount);
    if (!amount || amount <= 0) return;
    await onAddPayment({
      amount,
      paymentDate: paymentDraft.paymentDate || todayYmd(),
      method: paymentDraft.method || null,
      note: paymentDraft.note || null,
    });
    setPaymentDraft({
      amount: '',
      paymentDate: todayYmd(),
      method: 'credit_card',
      note: '',
    });
  };

  const methodLabel = (method) => {
    if (!method) return '—';
    return t(`partnerFinance.methods.${method}`, { defaultValue: method });
  };

  return (
    <Box
      sx={{
        mt: embedded ? 0 : (dense ? 2 : 0),
        p: embedded ? 0 : (dense ? 2 : 2.5),
        bgcolor: embedded ? 'transparent' : DOCUMENT.surface,
        border: embedded ? 0 : `1px solid ${DOCUMENT.ruleHard}`,
      }}
      data-testid={testId}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
          mb: 2,
          flexWrap: 'wrap',
        }}
      >
        {showTitle ? (
          <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: dense ? 17 : 20 }}>
            {title || (variant === 'group' ? t('partnerFinance.groupPayments') : t('partnerFinance.title'))}
          </Typography>
        ) : (
          <span />
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          {showStatusChip ? (
            <StatusBadge status={status} testId={`${testId}-status`} />
          ) : null}
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel id={`${testId}-currency-label`}>{t('partnerFinance.currency')}</InputLabel>
            <Select
              labelId={`${testId}-currency-label`}
              label={t('partnerFinance.currency')}
              value={currency}
              onChange={(e) => patchForm({ currency: e.target.value })}
              sx={{ borderRadius: 0 }}
            >
              {PARTNER_FINANCE_CURRENCIES.map((code) => (
                <MenuItem key={code} value={code}>{code}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: (showClient || showRollup) && showAgency ? '1fr 1fr' : '1fr',
          },
          gap: 3,
          mb: 2,
        }}
      >
        {showRollup ? (
          <Box>
            <Typography sx={sectionLabelSx}>{t('partnerFinance.clientPaymentSection')}</Typography>
            <MoneyLine label={t('partnerFinance.groupTotal')} value={formatMoney(rollup?.total, currency)} />
            <MoneyLine label={t('partnerFinance.groupPaid')} value={formatMoney(rollup?.paid ?? 0, currency)} />
            <MoneyLine
              label={t('partnerFinance.groupOutstanding')}
              value={
                rollup?.outstanding == null
                  ? '—'
                  : t('partnerFinance.remaining', { amount: formatMoney(rollup.outstanding, currency) })
              }
              remaining={rollup?.outstanding > 0}
              emphasize
            />
          </Box>
        ) : null}

        {showClient && !showRollup ? (
          <Box>
            <Typography sx={sectionLabelSx}>{t('partnerFinance.clientPaymentSection')}</Typography>
            <TextField
              size="small"
              type="number"
              fullWidth
              label={t('partnerFinance.tripValue')}
              value={form.tripValue}
              onChange={(e) => patchForm({ tripValue: e.target.value })}
              InputProps={{ sx: fieldSx }}
              sx={{ mb: 1.5 }}
              helperText={form.tripValue !== '' ? formatMoney(Number(form.tripValue), currency) : ' '}
            />
            <MoneyLine
              label={t('partnerFinance.clientPaid')}
              value={formatMoney(paidAmount || 0, currency)}
            />
            <MoneyLine
              label={t('partnerFinance.balanceDue')}
              value={
                balance == null
                  ? '—'
                  : t('partnerFinance.remaining', { amount: formatMoney(balance, currency) })
              }
              remaining={balance > 0}
              emphasize
            />
            <TextField
              size="small"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              label={t('partnerFinance.nextPaymentDue')}
              value={form.paymentDueDate}
              onChange={(e) => patchForm({ paymentDueDate: e.target.value })}
              InputProps={{ sx: fieldSx }}
              sx={{ mt: 0.5 }}
            />
          </Box>
        ) : null}

        {showAgency ? (
          <Box>
            <Typography sx={sectionLabelSx}>{t('partnerFinance.agencyEarningsSection')}</Typography>
            <TextField
              size="small"
              type="number"
              fullWidth
              label={t('partnerFinance.commissionExpected')}
              value={form.commissionExpected}
              onChange={(e) => patchForm({ commissionExpected: e.target.value })}
              InputProps={{ sx: fieldSx }}
              sx={{ mb: 1.5 }}
              helperText={
                form.commissionExpected !== ''
                  ? formatMoney(Number(form.commissionExpected), currency)
                  : ' '
              }
            />
            <TextField
              size="small"
              type="number"
              fullWidth
              label={t('partnerFinance.commissionReceived')}
              value={form.commissionReceived}
              onChange={(e) => patchForm({ commissionReceived: e.target.value })}
              InputProps={{ sx: fieldSx }}
              sx={{ mb: 1.5 }}
              helperText={
                form.commissionReceived !== ''
                  ? formatMoney(Number(form.commissionReceived), currency)
                  : ' '
              }
            />
            <MoneyLine
              label={t('partnerFinance.commissionOutstanding')}
              value={commissionOutstanding == null ? '—' : formatMoney(commissionOutstanding, currency)}
              remaining={commissionOutstanding > 0}
              emphasize
            />
          </Box>
        ) : null}
      </Box>

      {showHistory ? (
        <Box sx={{ mb: 2.5 }} data-testid={`${testId}-history`}>
          <Typography sx={sectionLabelSx}>{t('partnerFinance.paymentHistory')}</Typography>
          {payments.length === 0 ? (
            <Typography sx={{ fontSize: 13, color: DOCUMENT.ink3, mb: 1.5 }}>
              {t('partnerFinance.noPaymentsYet')}
            </Typography>
          ) : (
            <Table size="small" sx={{ mb: 1.5, '& td, & th': { borderColor: DOCUMENT.rule } }}>
              <TableHead>
                <TableRow>
                  <TableCell>{t('partnerFinance.paymentDate')}</TableCell>
                  <TableCell>{t('partnerFinance.paymentAmount')}</TableCell>
                  <TableCell>{t('partnerFinance.paymentMethod')}</TableCell>
                  <TableCell>{t('partnerFinance.paymentNote')}</TableCell>
                  <TableCell padding="checkbox" />
                </TableRow>
              </TableHead>
              <TableBody>
                {payments.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell>{p.paymentDate || '—'}</TableCell>
                    <TableCell>{formatMoney(p.amount, currency)}</TableCell>
                    <TableCell>{methodLabel(p.method)}</TableCell>
                    <TableCell>{p.note || '—'}</TableCell>
                    <TableCell padding="checkbox">
                      {onDeletePayment ? (
                        <IconButton
                          size="small"
                          aria-label={t('partnerFinance.deletePayment')}
                          onClick={() => onDeletePayment(p.id)}
                          disabled={paymentSaving}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr auto' },
              gap: 1.5,
              alignItems: 'end',
            }}
          >
            <TextField
              size="small"
              type="number"
              label={t('partnerFinance.paymentAmount')}
              value={paymentDraft.amount}
              onChange={(e) => setPaymentDraft((p) => ({ ...p, amount: e.target.value }))}
              InputProps={{ sx: fieldSx }}
            />
            <TextField
              size="small"
              type="date"
              InputLabelProps={{ shrink: true }}
              label={t('partnerFinance.paymentDate')}
              value={paymentDraft.paymentDate}
              onChange={(e) => setPaymentDraft((p) => ({ ...p, paymentDate: e.target.value }))}
              InputProps={{ sx: fieldSx }}
            />
            <FormControl size="small">
              <InputLabel>{t('partnerFinance.paymentMethod')}</InputLabel>
              <Select
                label={t('partnerFinance.paymentMethod')}
                value={paymentDraft.method}
                onChange={(e) => setPaymentDraft((p) => ({ ...p, method: e.target.value }))}
                sx={{ borderRadius: 0 }}
              >
                {['credit_card', 'bank_transfer', 'cash', 'check', 'other'].map((m) => (
                  <MenuItem key={m} value={m}>{methodLabel(m)}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              label={t('partnerFinance.paymentNote')}
              value={paymentDraft.note}
              onChange={(e) => setPaymentDraft((p) => ({ ...p, note: e.target.value }))}
              InputProps={{ sx: fieldSx }}
              sx={{ gridColumn: { xs: '1', sm: '1 / span 3' } }}
            />
            <Button
              onClick={handleAddPayment}
              disabled={paymentSaving || !paymentDraft.amount}
              sx={{ ...partnerOxbloodBtnSx, height: 40 }}
            >
              {t('partnerFinance.addPayment')}
            </Button>
          </Box>
        </Box>
      ) : null}

      <Button onClick={onSave} disabled={saving} sx={partnerOutlineBtnSx}>
        {saving ? t('partnerLists.loadingShort') : t('partnerFinance.save')}
      </Button>
    </Box>
  );
};

export default PartnerTripFinancePanel;
